import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "extreme-map" }
const _hoisted_2 = { class: "ol-map-wrapper" }
const _hoisted_3 = ["id"]

import EMMovieLayerManager from 'src/lib/extreme_map/EMMovieLayerManager';
import EMQueryAreaLayerManager from 'src/lib/extreme_map/EMQueryAreaLayerManager';
import EMKilopostLayerManager from 'src/lib/extreme_map/EMKilopostLayerManager';
import {
  computed,
  getCurrentInstance,
  onMounted,
  onUnmounted,
  reactive,
  ref,
  watch,
} from 'vue';
import OlMapWrapper, { type MapFeatureInfo, type PopupLayer } from 'src/lib/OlMapWrapper';
import { useExtremeMapViewControl } from 'src/components/lib/ExtremeMap/composables/useExtremeMapViewControl';
import { useEnv } from 'src/composables/useEnv';
import type { GisLayer, Settings as UserSettings } from 'src/models/user';
import type {
  ExtremeMapEssentials,
  GeoConnection,
  IExtremeMap,
  KpMap,
  Location,
  MapElemInfo,
  QueryArea,
} from 'src/models';
import type EMAbstractLayerManager from 'src/lib/extreme_map/EMAbstractLayerManager';
import type MapBrowserEvent from 'ol/MapBrowserEvent';
import type { JointFeatureCollection, PoleFeatureCollection } from 'src/models/apis/getGeoInfoResponse';
import type {
  GeoItemMeta,
  GIMovieList,
  GIResource,
  GIDebugMTX,
} from 'src/models/geoItem';

interface LayerZIndexMap {
  kpLine: number;
  kp: number;
  car: number;
  queryArea: number;
  movie: number;
  currentLayerZIndex: number;
}

interface ExtremeMapState {
  isDisplayInitialized: boolean;
  queryAreaLayerMgr: EMQueryAreaLayerManager;
  movieLayerMgr: EMMovieLayerManager;
  kpLayerMgr: EMKilopostLayerManager;
  mapHeight: number;
  popupLayerObj: PopupLayer | null;
  userSettings: UserSettings;
  kpMap: KpMap;
  geoConnections: Record<string, Record<string, GeoConnection>>;
}

interface Props {
  mapEssentials?: ExtremeMapEssentials | null;
  disableMovieLayerInteraction: boolean;
  // 親が地図を表示・サイズ確定させた後に true へ切り替える。
  // この信号を受けて初期表示範囲へフィットする。
  // MoviePlayer系の呼び出しでは初期フィット不要で省略されるため optional。
  // withDefaultsでdefaultを与えても、TS上optional(?)でないとrequired:trueのままになるVueの仕様
  // https://github.com/vuejs/core/blob/v3.5.35/packages/compiler-sfc/src/script/resolveType.ts (required: !e.optional)
  shouldFitInitialExtent?: boolean;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'index',
  props: {
    mapEssentials: { default: null },
    disableMovieLayerInteraction: { type: Boolean },
    shouldFitInitialExtent: { type: Boolean, default: false }
  },
  emits: ["all-deselected", "click-map", "zoom-changed", "click-item"],
  setup(__props: any, { expose: __expose, emit: __emit }) {

const props = __props;

const emit = __emit;

const { isDebugShowGeoConnections, isDebugShowKpAllLayer } = useEnv();
const initLayerZIndexMap = (): LayerZIndexMap => {
  return {
    kpLine: 10,
    kp: 15,
    car: 50,
    queryArea: 80,
    movie: 81,
    currentLayerZIndex: 1000,
  };
};
const layerZIndexMap = initLayerZIndexMap();
let onResizeFunc: () => void = () => {};

const state = reactive<ExtremeMapState>({
  isDisplayInitialized: false,
  queryAreaLayerMgr: new EMQueryAreaLayerManager({
    dataName: 'queryArea', giManager: null,
  }),
  movieLayerMgr: new EMMovieLayerManager({
    dataName: 'movie', giManager: null,
  }),
  kpLayerMgr: new EMKilopostLayerManager({
    isDebugShowGeoConnections,
    isDebugShowKpAllLayer,
  }),
  mapHeight: window.innerHeight - 160,
  popupLayerObj: null,
  // vue管理しない
  // layerMgrMap: {},
  userSettings: {} as UserSettings,
  kpMap: new Map(),
  geoConnections: {},
});
const currentInstance = getCurrentInstance();
const uid = currentInstance?.uid;
const layerMgrMap: Record<string, EMAbstractLayerManager> = {};

const olMap = new OlMapWrapper();
const hideQueryArea = () => {
  state.queryAreaLayerMgr.hideQueryArea();
};
const hidePopup = () => {
  if (state.popupLayerObj === null) return;

  const { popupOverlay, popupContent } = state.popupLayerObj;
  popupContent.innerHTML = '';
  popupOverlay.setPosition(undefined);
};
const notifyDeselectAll = () => {
  hideQueryArea();
  state.movieLayerMgr.deselectAll();
  // 各データレイヤー
  for (const ent of Object.entries(layerMgrMap)) {
    const layerMgr = ent[1];
    layerMgr.deselectAll();
  }
  // ポップアップ
  hidePopup();
  // 外部
  emit('all-deselected');
};
const clickedMapBackground = (evt: MapBrowserEvent) => {
  // 各レイヤーや外部に、別のとこが選択されたことを通知する
  notifyDeselectAll();

  let [lon, lat] = olMap.convCoord(
    { lon: evt.coordinate[0], lat: evt.coordinate[1] },
    { srcProj: 'EPSG:3857', destProj: 'EPSG:4326' },
  );
  lon = parseFloat(lon.toFixed(6));
  lat = parseFloat(lat.toFixed(6));
  emit('click-map', { lon, lat });
};
const clickedMapFeature = (evt: MapBrowserEvent, info: MapFeatureInfo) => {
  // 各レイヤーや外部に、別のとこが選択されたことを通知する
  notifyDeselectAll();

  if (info.name === 'poles') {
    olMap.showPolePopupFromFeatureInfo(
      state.popupLayerObj as PopupLayer, info.data as PoleFeatureCollection, evt.coordinate);
  } else if (info.name === 'joints') {
    olMap.showJointPopupFromFeatureInfo(
      state.popupLayerObj as PopupLayer, info.data as JointFeatureCollection, evt.coordinate);
  }
};
const setMapClickEvent = () => {
  olMap.onMapSingleClick((evt: MapBrowserEvent) => {
    if (evt.originalEvent.defaultPrevented) return;

    olMap.getMapFeatureInfo(evt).then((info: MapFeatureInfo) => {
      if (!info) {
        clickedMapBackground(evt);
      } else {
        clickedMapFeature(evt, info);
      }
    });
  });
};
const setMapMoveEndEvent = () => {
  let currentZoom = olMap.getZoom();
  olMap.onMapMoveEnd(() => {
    const newZoom = olMap.getZoom();
    if (newZoom !== currentZoom) {
      const evtObj = { zoom: newZoom, oldZoom: currentZoom };
      state.kpLayerMgr.refreshLayersOnZoomChange(evtObj);
      state.movieLayerMgr.refreshLayerOnZoomChange(evtObj);
      emit('zoom-changed', evtObj);
      currentZoom = newZoom;
    }
    const moveEndEvtObj = {
      zoom: newZoom,
      extent: olMap.getExtent(),
    };
    state.kpLayerMgr.refreshLayersOnMoveEnd(moveEndEvtObj);
  });
};
const initResizeFunc = () => {
  // resize map on window resize
  onResizeFunc = () => {
    const elem = document.getElementById(olMapId.value);
    if (elem === null) return;

    elem.style.height = `${state.mapHeight}px`;
    olMap.updateMapSize();
  };
  onResizeFunc();
  window.addEventListener('resize', onResizeFunc);
};
const initializeQueryAreaLayer = () => {
  const { layer } = state.queryAreaLayerMgr.prepareLayer();
  if (!layer) return;

  layer.setZIndex(layerZIndexMap.queryArea);
  olMap.addLayer(layer);
};
const initializeMovieLayer = () => {
  const { layer, layerInfo } = state.movieLayerMgr.prepareLayer([]);
  if (!layer) return;

  layer.setZIndex(layerZIndexMap.movie);
  olMap.addLayer(layer, {
    interaction: {
      click: layerInfo.onLayerClick,
    },
  });
  state.movieLayerMgr.connectWithExtremeMap(extremeMapInterface);
};

const refPopupContainer = ref<HTMLElement>();
const refPopupCloser = ref<HTMLElement>();
const refPopupContent = ref<HTMLElement>();
onMounted(() => {
  olMap.initMapManager({
    element: `ol-map-${uid}`,
    center: olMap.convCoord({lat: 35.679100, lon: 139.756932}),
    maxZoom: 18,
    minZoom: 4,
    zoom: 10,
    enablePan: true,
    enableZoomButton: true,
    enableMouseWheelZoom: true,
    enableDoubleClickZoom: true,
    enableScaleLine: true,
    enableAttribution: true,
  });

  olMap.addLayer(olMap.getKokudoChiriinLayer());

  if (refPopupContainer.value && refPopupCloser.value && refPopupContent.value) {
    state.popupLayerObj = olMap.getPopupLayer({
      popupContainer: refPopupContainer.value,
      popupCloser: refPopupCloser.value,
      popupContent: refPopupContent.value,
    });
    olMap.addPopupLayer(state.popupLayerObj as PopupLayer);
  }

  setMapClickEvent();
  setMapMoveEndEvent();
  olMap.enableLayerSwitcher();
  initResizeFunc();
  initializeQueryAreaLayer();

  // 今後のrefactorでmovieLayerManagerも外から渡すようにすれば
  // いらなくなるので消したい.
  state.movieLayerMgr.disableInteraction = props.disableMovieLayerInteraction;
  initializeMovieLayer();
});
onUnmounted(() => {
  window.removeEventListener('resize', onResizeFunc);
});

const initKpLayer = () => {
  const { kpLineLayer, kpLayerFiltered, kpLayerAll } =
    state.kpLayerMgr.prepareLayers(state.userSettings, state.kpMap, state.geoConnections);
  if (!kpLayerFiltered || !kpLineLayer) return;

  kpLayerFiltered.setZIndex(layerZIndexMap.kp);
  kpLineLayer.setZIndex(layerZIndexMap.kpLine);
  if (kpLayerAll) {
    kpLayerAll.setZIndex(layerZIndexMap.kp);
    olMap.addLayer(kpLayerAll);
  }
  olMap.addLayer(kpLayerFiltered);
  olMap.addLayer(kpLineLayer);
};

const initDisplay = async({ userSettings, kpMap, geoConnections }: ExtremeMapEssentials) => {
  if (state.isDisplayInitialized) return;

  state.isDisplayInitialized = true;
  state.userSettings = userSettings;
  state.kpMap = kpMap;
  state.geoConnections = geoConnections;

  const initialMapPos = userSettings.initialMapPos;
  olMap.setCenter(olMap.convCoord(initialMapPos));

  // 初期表示範囲へのフィットは地図が表示・サイズ確定した後に行う必要があるため
  // ここでは実行せず、親からの shouldFitInitialExtent prop を監視して実行する。

  userSettings.gisLayers.forEach((e: GisLayer, i: number) => {
    const layer = olMap.getGISLayer(e.layerName, e.dispName, i);
    olMap.addLayer(layer);
  });

  initKpLayer();
};

watch(() => props.shouldFitInitialExtent, (shouldFit) => {
  if (!shouldFit) return;

  applyInitialExtent();
});

watch(() => props.mapEssentials, async() => {
  if (!props.mapEssentials) return;

  await initDisplay(props.mapEssentials);
});

// computed
const olMapId = computed(() => {
  return `ol-map-${uid}`;
});

// 視点操作（初期範囲フィット・動画リスト範囲フィット・中心移動）
const { applyInitialExtent, fitToMovieListsExtent, moveCenterTo } = useExtremeMapViewControl({
  olMap,
  olMapId,
  getUserSettings: () => state.userSettings,
});

// methods:
const triggerResize = () => {
  onResizeFunc();
};
const setMapHeight = (h: number) => {
  const prevH = state.mapHeight;
  state.mapHeight = h;
  if (h !== prevH) {
    triggerResize();
  }
};
const refreshMovieLayer = (movieLists: GIMovieList[]) => {
  state.movieLayerMgr.refreshLayer(movieLists);
};
// 指定されたレイヤーを除いたレイヤーの選択表示を解除する
const deselectLayersExcept = (exceptLayer: string) => {
  if (exceptLayer !== 'movie') {
    state.movieLayerMgr.deselectAll();
  }
  // 各データレイヤー
  for (const [dataName, layerMgr] of Object.entries(layerMgrMap)) {
    if (exceptLayer !== dataName) {
      layerMgr.deselectAll();
    }
  }
  // ポップアップ
  hidePopup();
};
const showQueryArea = (queryArea: QueryArea) => {
  const radiusDeg = olMap.getDegreesPerMeter() * queryArea.radius;
  const center = [queryArea.lon, queryArea.lat];
  const edge = [center[0] + radiusDeg, center[1] + radiusDeg];
  const groundRadius = olMap.getCoordDistance(center, edge);
  queryArea.dispRadius = groundRadius;
  state.queryAreaLayerMgr.showQueryArea(queryArea);
};
const showQueryPin = (queryArea: QueryArea) => {
  state.queryAreaLayerMgr.showQueryPin(queryArea);
};
const showDataLayer = (metaItem: GeoItemMeta, resources: GIResource[] | GIDebugMTX, zIndexOffset = 0) => {
  removeDataLayer(metaItem.name);
  const { layer, layerInfo } = metaItem.layerManager.prepareLayer(resources);
  const { additionalLayers, additionalLayerInfos } = metaItem.layerManager.prepareAdditionalLayers(resources);
  const layers = layer ? [layer, ...additionalLayers] : additionalLayers;
  const layerInfos = [layerInfo, ...additionalLayerInfos];
  const zIndexBase = metaItem.name in layerZIndexMap
    ? layerZIndexMap[metaItem.name as keyof LayerZIndexMap]
    : layerZIndexMap.currentLayerZIndex + zIndexOffset;
  layers.forEach((elem, idx) => {
    // 通常layerと被らないように、addtional layerは1桁増やす
    const zIndex = elem === layer ? zIndexBase : zIndexBase * 10 + idx;
    elem.setZIndex(zIndex);
    olMap.addLayer(elem, {
      interaction: { click: layerInfos[idx].onLayerClick },
    });
  });
  metaItem.layerManager.connectWithExtremeMap(extremeMapInterface);
  layerMgrMap[metaItem.name] = metaItem.layerManager;
};
const removeDataLayer = (layerName: string) => {
  if (!layerMgrMap[layerName]) return;

  const { layer } = layerMgrMap[layerName].getLayer();
  const { additionalLayers } = layerMgrMap[layerName].getAdditionalLayers();
  const layers = layer ? [layer, ...additionalLayers] : additionalLayers;
  layers.forEach(layer => {
    if (!layer.name) return;

    olMap.removeLayer(layer.name);
  });
  layerMgrMap[layerName].destroy();
  delete layerMgrMap[layerName];
};
/**
 * layerManager側で発火したイベントを受け取るための関数群
 * (とりあえずclickしかないが)
 */
const handleLayerManagerEventClick = (evtObj: MapElemInfo<GIResource>) => {
  emit('click-item', evtObj);
};
// layerManager に渡す ExtremeMap 参照。
// Vue3 の instance proxy は <script setup> 由来メソッドを own property として持たず、
// layerManager 側の hasOwnProperty/関数判定に通らないため、明示的に実装オブジェクトを渡す。
const extremeMapInterface: IExtremeMap = {
  showDataLayer,
  removeDataLayer,
  handleLayerManagerEventClick,
};
// 与えられたlat, lonに一番近いmgiを取得して返す.
const getClosestMgi = (point: Location, searchOpts: { featureIdPrefix?: number } = {}) => {
  return state.movieLayerMgr.getClosestMgi(point, searchOpts);
};
__expose({
  triggerResize,
  setMapHeight,
  refreshMovieLayer,
  fitToMovieListsExtent,
  deselectLayersExcept,
  showQueryArea,
  showQueryPin,
  showDataLayer,
  removeDataLayer,
  handleLayerManagerEventClick,
  getClosestMgi,
  moveCenterTo,
  hideQueryArea,
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", {
        id: olMapId.value,
        class: "ol-map"
      }, null, 8, _hoisted_3),
      _createElementVNode("div", {
        ref_key: "refPopupContainer",
        ref: refPopupContainer,
        class: "ol-popup"
      }, [
        _createElementVNode("a", {
          href: "#",
          ref_key: "refPopupCloser",
          ref: refPopupCloser,
          class: "ol-popup-closer"
        }, null, 512),
        _createElementVNode("div", {
          ref_key: "refPopupContent",
          ref: refPopupContent,
          class: "ol-popup-content"
        }, null, 512)
      ], 512)
    ])
  ]))
}
}

})